/**
 * Activity Log Service
 *
 * Writes to the `activity_log` table to record every create, update, and delete
 * action across all entities. Each entry captures who performed the action,
 * what changed, and when.
 *
 * Usage: call `activityLogService.log(...)` after any successful mutation.
 * The log call is fire-and-forget — failures are silently caught so they
 * never block the user-facing operation.
 */

import { supabase } from '@/lib/supabase'
import { profileService } from './profileService'

export type ActivityAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'uploaded'
  | 'linked'
  | 'unlinked'
  | 'status_changed'
  | 'imported'
  | 'exported'
  | 'duplicated'

export type EntityType =
  | 'product'
  | 'component'
  | 'document'
  | 'supplier'
  | 'checklist'
  | 'checklist_item'
  | 'product_component'
  | 'supplier_contact'
  | 'supplier_document'
  | 'supplier_audit'
  | 'qms_action'
  | 'quality_objective'
  | 'quality_metric'
  | 'nonconformance'
  | 'internal_audit'
  | 'audit_finding'
  | 'external_audit'
  | 'external_reference'
  | 'mrm_review'
  | 'custom_field_definition'
  | 'team_member'
  | 'organization'

interface LogParams {
  action: ActivityAction
  entityType: EntityType
  entityId: string
  entityName?: string
  details?: Record<string, unknown>
}

export const activityLogService = {
  /**
   * Log an activity. Fire-and-forget — never throws.
   */
  async log(params: LogParams): Promise<void> {
    try {
      const [orgId, { data: { user } }] = await Promise.all([
        profileService.getOrganizationId(),
        supabase.auth.getUser(),
      ])

      if (!orgId || !user) return

      await supabase.from('activity_log').insert({
        organization_id: orgId,
        user_id: user.id,
        action: params.action,
        entity_type: params.entityType,
        entity_id: params.entityId,
        details: {
          entity_name: params.entityName ?? null,
          ...params.details,
        },
      })
    } catch {
      // Silently swallow — audit logging must never break user flows
    }
  },

  /**
   * Log multiple activities in one call (e.g. bulk import).
   */
  async logBatch(entries: LogParams[]): Promise<void> {
    if (entries.length === 0) return

    try {
      const [orgId, { data: { user } }] = await Promise.all([
        profileService.getOrganizationId(),
        supabase.auth.getUser(),
      ])

      if (!orgId || !user) return

      const rows = entries.map((params) => ({
        organization_id: orgId,
        user_id: user.id,
        action: params.action,
        entity_type: params.entityType,
        entity_id: params.entityId,
        details: {
          entity_name: params.entityName ?? null,
          ...params.details,
        },
      }))

      await supabase.from('activity_log').insert(rows)
    } catch {
      // Silently swallow
    }
  },

  /**
   * Get recent activity for the current organisation.
   */
  async getRecentActivity(limit = 50): Promise<Array<{
    id: string
    action: string
    entity_type: string
    entity_id: string
    details: Record<string, unknown> | null
    created_at: string
    user_id: string
    user_name: string | null
  }>> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('activity_log')
      .select('*, profile:profiles!user_id(full_name)')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Error fetching activity log:', error)
      return []
    }

    type ActivityRow = {
      id: string
      action: string
      entity_type: string
      entity_id: string
      details: Record<string, unknown> | null
      created_at: string
      user_id: string
      profile: { full_name: string | null } | null
    }

    return ((data ?? []) as ActivityRow[]).map((row) => ({
      id: row.id,
      action: row.action,
      entity_type: row.entity_type,
      entity_id: row.entity_id,
      details: row.details,
      created_at: row.created_at,
      user_id: row.user_id,
      user_name: row.profile?.full_name ?? null,
    }))
  },

  /**
   * Get activity for a specific entity.
   */
  async getEntityActivity(entityType: EntityType, entityId: string): Promise<Array<{
    id: string
    action: string
    details: Record<string, unknown> | null
    created_at: string
    user_id: string
    user_name: string | null
  }>> {
    const { data, error } = await supabase
      .from('activity_log')
      .select('*, profile:profiles!user_id(full_name)')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching entity activity:', error)
      return []
    }

    type ActivityRow = {
      id: string
      action: string
      details: Record<string, unknown> | null
      created_at: string
      user_id: string
      profile: { full_name: string | null } | null
    }

    return ((data ?? []) as ActivityRow[]).map((row) => ({
      id: row.id,
      action: row.action,
      details: row.details,
      created_at: row.created_at,
      user_id: row.user_id,
      user_name: row.profile?.full_name ?? null,
    }))
  },
}
